import type { IpcStatusBadgeVariant } from '../components/ui/IpcStatusBadge';
import type { SpecScheduleStatus } from '../types';

/** 节点进度（含只读终止） */
export type NodeVisualStatus = 'pending' | 'in_progress' | 'completed' | 'terminated';

/**
 * 业务状态 → 视觉映射（唯一权威）。
 * 实际颜色由 :root `--ipc-status-*` / `--color-*` 控制。
 */

export function normalizeSpecScheduleStatus(status?: string | null): SpecScheduleStatus {
  const s = (status ?? '').trim().toLowerCase();
  if (s === 'completed') return 'completed';
  if (s === 'terminated') return 'terminated';
  // 旧码 paused / dev_completed 视为进行中
  return 'in_progress';
}

/** erpa_base.style_label → IpcStatusBadge 色：灰/蓝/绿/红 */
export function styleLabelToBadgeVariant(styleLabel?: string | null): IpcStatusBadgeVariant {
  const s = (styleLabel ?? '').trim().toLowerCase();
  if (s === 'success') return 'success';
  if (s === 'processing' || s === 'primary') return 'primary';
  if (s === 'error' || s === 'danger' || s === 'destructive') return 'destructive';
  if (s === 'warning' || s === 'warn') return 'warning';
  if (s === 'default' || s === 'muted') return 'muted';
  return 'default';
}

export function projectStatusBadgeVariant(status?: string | null): IpcStatusBadgeVariant {
  const s = (status ?? '').trim().toLowerCase();
  if (s === 'completed') return 'success';
  if (s === 'in_progress') return 'primary';
  if (s === 'planning') return 'muted';
  if (s === 'terminated') return 'destructive';
  return 'default';
}

export function specStatusBadgeVariant(status?: string | null): IpcStatusBadgeVariant {
  const s = normalizeSpecScheduleStatus(status);
  if (s === 'completed') return 'success';
  if (s === 'terminated') return 'destructive';
  // 进行中与项目/节点一致：蓝色
  return 'primary';
}

export function normalizeNodeVisualStatus(status?: string | null): NodeVisualStatus {
  const s = (status ?? '').trim().toLowerCase();
  if (s === 'terminated') return 'terminated';
  if (s === 'completed' || s === 'done') return 'completed';
  if (s === 'in_progress' || s === 'active') return 'in_progress';
  return 'pending';
}

/** 排期单元格等：背景色走 CSS 变量，与节点/标签同色系 */
export function nodeStatusCellBackground(status?: string | null): string {
  switch (normalizeNodeVisualStatus(status)) {
    case 'completed':
      return 'var(--ipc-status-completed-bg)';
    case 'in_progress':
      return 'var(--ipc-status-in-progress-bg)';
    case 'terminated':
      return 'var(--ipc-status-terminated-bg)';
    default:
      return 'var(--ipc-status-pending-bg)';
  }
}

/** 纯色文字/圆点：监控分析等内联色 */
export function nodeStatusForegroundVar(status?: string | null): string {
  switch (normalizeNodeVisualStatus(status)) {
    case 'completed':
      return 'var(--ipc-status-completed)';
    case 'in_progress':
      return 'var(--ipc-status-in-progress)';
    case 'terminated':
      return 'var(--ipc-status-terminated)';
    default:
      return 'var(--ipc-status-pending)';
  }
}
